import React, { useState, useEffect } from 'react';
import { Calendar, Heart, CalendarDays, User } from 'lucide-react';
import quotesData from './data/quotes.json';
import { getTodayDate, parseDate } from './utils/dateUtils';
import TodayQuote from './components/TodayQuote';
import Favorites from './components/Favorites';
import QuoteCalendar from './components/QuoteCalendar';
import AuthModal from './components/AuthModal';

type Quote = {
  id: number;
  text: string;
  author: string;
  date: string;
};

type FavoriteQuote = Quote & {
  collectedAt: number;
  insight?: string;
};

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'today' | 'favorites' | 'calendar'>('today');
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [todayQuote, setTodayQuote] = useState<Quote | null>(null);
  const [favorites, setFavorites] = useState<FavoriteQuote[]>([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    // Load quotes
    setQuotes(quotesData.quotes);

    // Set today's quote
    const today = getTodayDate();
    const todayQuote = quotesData.quotes.find(quote => quote.date === today) || null;
    setTodayQuote(todayQuote);

    // Load favorites from localStorage
    const storedFavorites = localStorage.getItem('favorites');
    if (storedFavorites) {
      setFavorites(JSON.parse(storedFavorites));
    }

    // Check login status
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      setIsLoggedIn(true);
      setUsername(user.displayName || user.email);
    }
  }, []);

  const handleLike = (quote: Quote) => {
    const existingIndex = favorites.findIndex(fav => fav.id === quote.id);
    if (existingIndex !== -1) {
      // Remove from favorites if already liked
      const newFavorites = favorites.filter(fav => fav.id !== quote.id);
      setFavorites(newFavorites);
    } else {
      // Add to favorites
      const newFavorite: FavoriteQuote = {
        ...quote,
        collectedAt: Date.now(),
      };
      const newFavorites = [...favorites, newFavorite];
      setFavorites(newFavorites);
    }
    // Update localStorage
    localStorage.setItem('favorites', JSON.stringify(favorites));
  };

  const handleRemoveFavorite = (id: number) => {
    const newFavorites = favorites.filter(fav => fav.id !== id);
    setFavorites(newFavorites);
    // Update localStorage
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };

  const handleUpdateInsight = (id: number, insight: string) => {
    const updatedFavorites = favorites.map(fav =>
      fav.id === id ? { ...fav, insight } : fav
    );
    setFavorites(updatedFavorites);
    // Update localStorage
    localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setIsLoggedIn(false);
    setUsername('');
    setIsAuthModalOpen(false);
  };

  const buttonClass = (tab: string) => `
    btn-primary flex items-center justify-center space-x-2
    w-full sm:w-32 h-12 px-4 py-2 rounded-md text-sm font-medium
    ${activeTab === tab ? 'bg-indigo-700' : 'bg-indigo-600 hover:bg-indigo-700'}
    transition-colors duration-200 ease-in-out
  `;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-indigo-50 to-purple-100">
      <header className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">Daily Wisdom</h1>
          <button
            onClick={() => setIsAuthModalOpen(true)}
            className="btn-secondary flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white"
          >
            <User size={20} />
            <span>{isLoggedIn ? username : 'Login'}</span>
          </button>
        </div>
      </header>

      <main className="flex-grow container mx-auto p-4">
        <div className="flex justify-center space-x-4 mb-6">
          <button onClick={() => setActiveTab('today')} className={buttonClass('today')}>
            <Calendar size={20} />
            <span>Today</span>
          </button>
          <button onClick={() => setActiveTab('favorites')} className={buttonClass('favorites')}>
            <Heart size={20} />
            <span>Faves</span>
          </button>
          <button onClick={() => setActiveTab('calendar')} className={buttonClass('calendar')}>
            <CalendarDays size={20} />
            <span>Cal</span>
          </button>
        </div>

        {activeTab === 'today' && todayQuote && (
          <TodayQuote
            quote={todayQuote}
            onLike={handleLike}
            favorites={favorites}
            onUpdateInsight={handleUpdateInsight}
          />
        )}
        {activeTab === 'favorites' && (
          <Favorites
            favorites={favorites}
            onRemove={handleRemoveFavorite}
            onUpdateInsight={handleUpdateInsight}
          />
        )}
        {activeTab === 'calendar' && (
          <QuoteCalendar
            quotes={quotes}
            todayQuote={todayQuote}
            onLike={handleLike}
            favorites={favorites}
            onUpdateInsight={handleUpdateInsight}
          />
        )}
      </main>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLogout={handleLogout}
        isLoggedIn={isLoggedIn}
        username={username}
      />
    </div>
  );
};

export default App;